import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import DocumentTab from 'src/parts/tabs/DocumentTab';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getEventDocuments } from 'src/api/admin/events';
import { getDocumentTypes } from 'src/api/admin/documentTypes';

interface DocumentsSearch {
	filter?: string;
	page?: number;
}

export const Route = createFileRoute('/_admin/events/$eventId/documents/')({
	validateSearch: (search: Record<string, unknown>): DocumentsSearch => ({
		filter: search.filter as string | undefined,
		page: search.page as number | undefined,
	}),
	component: DocumentsPage,
});

function DocumentsPage() {
	const { eventId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();

	const { data: documentsData, isLoading: documentsLoading } = useQuery({
		queryKey: ['event-documents', eventId, searchParams],
		queryFn: () => getEventDocuments(eventId, { filter: searchParams.filter, page: searchParams.page }),
	});

	const { data: documentTypesData, isLoading: typesLoading } = useQuery({
		queryKey: ['document-types', 'events'],
		queryFn: () => getDocumentTypes('events'),
	});

	const verifyPermission = authenticatedUser ? getVerifyPermission(authenticatedUser.permissions) : () => false;

	const revalidateEventDetail = () => {
		queryClient.invalidateQueries({ queryKey: ['event-documents', eventId] });
		queryClient.invalidateQueries({ queryKey: ['event', eventId] });
	};

	const handleReload = () => {
		revalidateEventDetail();
	};

	const handleSelectDocument = (id: string) => {
		navigate({ to: '/events/$eventId/documents/$documentId', params: { eventId, documentId: id } });
	};

	const isLoading = authLoading || documentsLoading || typesLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<DocumentTab
			data={documentsData}
			documentTypes={documentTypesData?.data || []}
			revalidateDetail={revalidateEventDetail}
			apiUrl={`/api/events/${eventId}/files`}
			onReload={handleReload}
			onSelectDocument={handleSelectDocument}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.adminEventsView]),
				fullView: verifyPermission([PERMISSIONS.adminEventsView]),
				upload: verifyPermission([PERMISSIONS.adminEventsEdit]),
				archive: verifyPermission([PERMISSIONS.adminEventsEdit]),
				edit: verifyPermission([PERMISSIONS.adminEventsEdit]),
			}}
		/>
	);
}
